
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { CondominiumWithSubscription } from "@/types/superadmin"
import { ORG_TYPE_PRESET, normalizeModuleSelection, type AppModule, type OrgType } from "@/lib/modules"

const sameModules = (a: readonly AppModule[], b: readonly AppModule[]) => {
  const x = normalizeModuleSelection(a)
  const y = normalizeModuleSelection(b)
  return x.length === y.length && x.every((m) => y.includes(m))
}

const setModules = async (condominiumId: string, modules: AppModule[]) => {
  const { error } = await supabase.rpc("set_condominium_modules", {
    condominium_uuid: condominiumId,
    modules: normalizeModuleSelection(modules),
  })
  if (error) throw error
}

export const useCondominiums = () => {
  return useQuery({
    queryKey: ["condominiums"],
    queryFn: async (): Promise<CondominiumWithSubscription[]> => {
      // "*" para não quebrar enquanto enabled_modules não existir no banco (cai no preset do tipo).
      const { data, error } = await supabase
        .from("condominiums")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) throw error
      
      return data as unknown as CondominiumWithSubscription[]
    }
  })
}

export const useCreateCondominium = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      name: string
      address: string
      subscription_months: number
      type: OrgType
      enabled_modules: AppModule[]
    }) => {
      const subscription_end = new Date()
      subscription_end.setMonth(subscription_end.getMonth() + data.subscription_months)

      // O banco aplica o preset do tipo; só chama o RPC quando a seleção foge dele.
      const { data: condominium, error } = await supabase
        .from("condominiums")
        .insert({
          name: data.name,
          address: data.address,
          subscription_expires_at: subscription_end.toISOString(),
          payment_status: 'active',
          type: data.type
        })
        .select()
        .single()

      if (error) throw error
      if (!sameModules(data.enabled_modules, ORG_TYPE_PRESET[data.type])) {
        await setModules(condominium.id, data.enabled_modules)
      }
      return condominium
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["condominiums"] })
      queryClient.invalidateQueries({ queryKey: ["superadmin-stats"] })
      toast.success("Organização criada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao criar organização:", error)
      toast.error("Erro ao criar organização")
    }
  })
}

export const useUpdateCondominium = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      id: string
      name: string
      address: string
      payment_status: string
      subscription_expires_at: string | null
      type: OrgType
      /** Só quando mudou. */
      enabled_modules?: AppModule[]
    }) => {
      const { error } = await supabase.rpc('update_condominium', {
        condominium_uuid: data.id,
        condominium_name: data.name,
        condominium_address: data.address,
        payment_status_val: data.payment_status,
        subscription_expires_at_val: data.subscription_expires_at,
        condominium_type: data.type
      })

      if (error) throw error
      if (data.enabled_modules) await setModules(data.id, data.enabled_modules)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["condominiums"] })
      queryClient.invalidateQueries({ queryKey: ["superadmin-stats"] })
      toast.success("Organização atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar organização:", error)
      toast.error("Erro ao atualizar organização")
    }
  })
}

export const useDeleteCondominium = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (condominiumId: string) => {
      const { error } = await supabase.rpc('delete_condominium_cascade', {
        condominium_uuid: condominiumId
      })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["condominiums"] })
      queryClient.invalidateQueries({ queryKey: ["superadmin-stats"] })
      toast.success("Organização e todos os dados relacionados foram deletados com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao deletar organização:", error)
      toast.error("Erro ao deletar organização")
    }
  })
}

export const useSetCondominiumModules = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, modules }: { id: string; modules: AppModule[] }) => setModules(id, modules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["condominiums"] })
      queryClient.invalidateQueries({ queryKey: ["condominium"] })
      toast.success("Módulos atualizados")
    },
    onError: (error) => {
      console.error("Erro ao atualizar módulos:", error)
      toast.error("Erro ao atualizar módulos. A migração de módulos já foi aplicada?")
    }
  })
}
